import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import store, { showToast, showToastWithType } from '../../../../core/store';
import { exportCasePlan, fetchCasePlanDetail, patchCasePlan } from '../../api/casePlanDetailApi';
import { fetchLearnerReentryPlan } from '../../api/learnerReentryPlanApi';
import {
  CASE_PLAN_EXPORT_SUCCESS_MESSAGE,
  canTransitionStatus,
  PLAN_STATUS,
  PLAN_STATUS_CONFIRM_COPY,
  PLAN_STATUS_UPDATE_FAILED_MESSAGE,
  PLAN_STATUS_UPDATE_FORBIDDEN_MESSAGE,
  PLAN_STATUS_UPDATE_INVALID_MESSAGE,
  PLAN_STATUS_UPDATE_SUCCESS_MESSAGE,
  REENTRY_PLANS_LIST_PATH,
} from '../../constants/reentry-planning.constants';
import { loadCasePlanDetailData } from '../../utils/reentry-plan-detail.util';
import { useCasePlanSection } from './useCasePlanSection';

const CONFIRMABLE_STATUSES = new Set(Object.keys(PLAN_STATUS_CONFIRM_COPY));

const getErrorStatus = (error) => error?.status ?? error?.statusCode;

export const useCasePlanDetailsHook = (casePlanId) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [cardProps, setCardProps] = useState(null);
  const [plan, setPlan] = useState(null);
  const [sections, setSections] = useState([]);
  const [isSectionsLoading, setIsSectionsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [planStatus, setPlanStatus] = useState(null);
  const [isEditor, setIsEditor] = useState(() => location.state?.isEditor === true);
  const [planId, setPlanId] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const handleBack = useCallback(() => {
    navigate(REENTRY_PLANS_LIST_PATH);
  }, [navigate]);

  // Keyed by the stable section key, not the section id, so collapse state survives a
  // pending section's temporary id being replaced by its server UUID.
  const handleToggleCollapse = useCallback((sectionKey) => {
    setCollapsedSections((previous) => ({
      ...previous,
      [sectionKey]: !previous[sectionKey],
    }));
  }, []);

  const handleAddWidget = useCallback((_sectionId) => {
    // Widget picker and persistence will be wired in a follow-up slice.
  }, []);

  const handleEditorsClick = useCallback(() => {
    // Editors modal will be wired in a follow-up slice.
  }, []);

  const handleExportClick = useCallback(() => {
    setIsExportOpen(true);
  }, []);

  const handleExportClose = useCallback(() => {
    setIsExportOpen(false);
  }, []);

  const handleExportSubmit = useCallback(async ({ sectionIds, emails, password }) => {
    try {
      await exportCasePlan(casePlanId, { sectionIds, emails, password });
      store.dispatch(showToastWithType(CASE_PLAN_EXPORT_SUCCESS_MESSAGE, 'success'));
    } catch {
      store.dispatch(showToast('Failed to export case plan. Please try again.'));
      throw new Error('Case plan export failed');
    }
  }, [casePlanId]);

  const handleStatusSelect = useCallback((value) => {
    if (value === planStatus || !canTransitionStatus(planStatus, value)) {
      return;
    }

    if (!CONFIRMABLE_STATUSES.has(value) || value === PLAN_STATUS.DRAFT) {
      return;
    }

    setPendingStatus(value);
  }, [planStatus]);

  const handleStatusCancel = useCallback(() => {
    setPendingStatus(null);
  }, []);

  /** Re-runs the detail load; used when a section mutation reveals stale local state. */
  const reloadDetail = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const resetLoadingState = useCallback(() => {
    setIsSectionsLoading(true);
    setError(null);
    setCardProps(null);
    setPlan(null);
    setSections([]);
    setPlanStatus(null);
    setIsEditor(location.state?.isEditor === true);
    setPlanId(null);
    setPendingStatus(null);
  }, [location.state]);

  const applyLoadedDetail = useCallback((detail) => {
    setCardProps(detail.cardProps);
    setPlan(detail.plan ?? null);
    setSections(detail.sections);
    const editor = detail.isEditor === true || detail.plan?.isEditor === true;
    setPlanStatus(detail.status ?? detail.plan?.status ?? null);
    setIsEditor(editor);
    setPlanId(detail.planId ?? detail.plan?.id ?? null);
  }, []);

  const applyLoadFailure = useCallback(() => {
    setCardProps(null);
    setSections([]);
    setError('Failed to load case plan details');
    setPlanStatus(null);
    setIsEditor(false);
    setPlanId(null);
    setPendingStatus(null);
  }, []);

  const finishLoading = useCallback(() => {
    setIsSectionsLoading(false);
  }, []);

  const applyMissingCasePlan = useCallback(() => {
    setError('Case plan not found');
    setIsSectionsLoading(false);
  }, []);

  const loadCasePlanDetail = useCallback(
    async (getIsCancelled, signal) => {
      resetLoadingState();

      try {
        const detail = await loadCasePlanDetailData(casePlanId, {
          fetchCasePlanDetail,
          fetchLearnerReentryPlan,
          signal,
        });

        if (getIsCancelled()) {
          return;
        }

        applyLoadedDetail(detail);
      } catch (loadError) {
        if (getIsCancelled() || loadError?.name === 'AbortError') {
          return;
        }

        applyLoadFailure();
      } finally {
        if (!getIsCancelled()) {
          finishLoading();
        }
      }
    },
    [casePlanId, resetLoadingState, applyLoadedDetail, applyLoadFailure, finishLoading],
  );

  const handleStatusConfirm = useCallback(async () => {
    const target = pendingStatus;
    if (!target || !casePlanId) {
      return;
    }

    try {
      const body = await patchCasePlan(casePlanId, { status: target });
      const nextStatus = body?.plan?.status ?? target;
      setPlanStatus(nextStatus);
      setPlan((previousPlan) =>
        previousPlan ? { ...previousPlan, status: nextStatus } : previousPlan,
      );
      setPendingStatus(null);
      store.dispatch(showToastWithType(PLAN_STATUS_UPDATE_SUCCESS_MESSAGE, 'success'));
    } catch (patchError) {
      setPendingStatus(null);
      const statusCode = getErrorStatus(patchError);
      if (statusCode === 403 || statusCode === 422) {
        store.dispatch(
          showToast(
            statusCode === 403
              ? PLAN_STATUS_UPDATE_FORBIDDEN_MESSAGE
              : PLAN_STATUS_UPDATE_INVALID_MESSAGE,
          ),
        );
        await loadCasePlanDetail(() => false);
        return;
      }

      store.dispatch(showToast(PLAN_STATUS_UPDATE_FAILED_MESSAGE));
    }
  }, [pendingStatus, casePlanId, loadCasePlanDetail]);

  useEffect(() => {
    if (!casePlanId) {
      applyMissingCasePlan();
      return;
    }

    const abortController = new AbortController();
    const getIsCancelled = () => abortController.signal.aborted;

    loadCasePlanDetail(getIsCancelled, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [casePlanId, loadCasePlanDetail, applyMissingCasePlan, reloadToken]);

  const sectionApi = useCasePlanSection({
    casePlanId,
    plan,
    sections,
    setSections,
    reloadDetail,
  });

  return {
    cardProps,
    plan,
    sections,
    isSectionsLoading,
    error,
    collapsedSections,
    handleBack,
    handleToggleCollapse,
    handleAddWidget,
    handleEditorsClick,
    handleExportClick,
    isExportOpen,
    handleExportClose,
    handleExportSubmit,
    isViewOnly: !sectionApi.canManageSections,
    planStatus,
    isEditor,
    planId,
    pendingStatus,
    isStatusModalOpen: Boolean(pendingStatus),
    handleStatusSelect,
    handleStatusConfirm,
    handleStatusCancel,
    sectionApi,
  };
};
